/**
 * Color and white bulb action preparations.
 * Duplicated from Scenes for Flow independence.
 */

import { COLOR_BULBS } from 'src/constants';
import { convertUiPropertyToSceneCommand } from '../utilities';
import { value_from_percent_range } from 'src/helpers/utilities';
import { isCctOnlyDevice, isRgbwwPm, isXMODDevice } from 'src/helpers/interface_helpers';
import type {
	DeviceActionContext,
	PreparedDeviceAction,
	ColorBulbActionName,
	ColorBulbActionHandler,
	CCTBulbActionName,
	CCTBulbActionHandler,
	DeviceActionGetter,
} from './types';

const handleColorTurn: ColorBulbActionHandler = ({ action, set }) => {
	set.state = action.value;
};

const handleColorMode: ColorBulbActionHandler = ({ action, set }) => {
	set.mode = action.value;
};

const handleColorTransitionDuration: ColorBulbActionHandler = ({ action, set }) => {
	set.transition_duration = action.value;
};

const handleColorTemperature: ColorBulbActionHandler = ({ action, set }) => {
	set.temp = action.value;
};

const handleColorRgb: ColorBulbActionHandler = ({ action, set, getAction }) => {
	if (action.name !== 'r') return;
	const green = getAction('g');
	const blue = getAction('b');
	if (!green || !blue) return;
	set.r = action.value;
	set.g = green.value;
	set.b = blue.value;
};

const handleColorBrightness: ColorBulbActionHandler = ({ action, set, getAction }) => {
	const brightnessValue = action.value;
	const modeAction = getAction('mode');
	const tempAction = getAction('temp', 'ct');
	if (tempAction || modeAction?.value === 'white') {
		set.brightness = brightnessValue;
		return;
	}
	if (modeAction?.value === 'color') {
		set.gain = brightnessValue;
		return;
	}
	set.gain = brightnessValue;
	set.brightness = brightnessValue;
};

const handleColorWhiteChannel: ColorBulbActionHandler = ({ action, set, context }) => {
	set[convertUiPropertyToSceneCommand('white_channel', context.channel)] = value_from_percent_range(
		action.value,
		[0, 255],
	);
};

const COLOR_BULB_ACTION_HANDLERS: Partial<Record<ColorBulbActionName, ColorBulbActionHandler>> = {
	turn: handleColorTurn,
	state: handleColorTurn,
	mode: handleColorMode,
	transition_duration: handleColorTransitionDuration,
	temp: handleColorTemperature,
	ct: handleColorTemperature,
	r: handleColorRgb,
	g: handleColorRgb,
	b: handleColorRgb,
	brightness: handleColorBrightness,
	gain: handleColorBrightness,
	white_channel: handleColorWhiteChannel,
};

const handleCctTurn: CCTBulbActionHandler = ({ action, set, context }) => {
	set[convertUiPropertyToSceneCommand('turn', context.channel)] = action.value;
};

const handleCctTemperature: CCTBulbActionHandler = ({ action, set, context }) => {
	set[convertUiPropertyToSceneCommand('ct', context.channel)] = action.value;
};

const handleCctBrightness: CCTBulbActionHandler = ({ action, set, context }) => {
	set[convertUiPropertyToSceneCommand('brightness', context.channel, 'white')] = action.value;
};

const handleCctTransitionDuration: CCTBulbActionHandler = ({ action, set, context }) => {
	set[convertUiPropertyToSceneCommand('transition_duration', context.channel)] = action.value;
};

const CCT_BULB_ACTION_HANDLERS: Partial<Record<CCTBulbActionName, CCTBulbActionHandler>> = {
	turn: handleCctTurn,
	state: handleCctTurn,
	temp: handleCctTemperature,
	ct: handleCctTemperature,
	brightness: handleCctBrightness,
	gain: handleCctBrightness,
	transition_duration: handleCctTransitionDuration,
};

export const prepareColorBulbAction = (context: DeviceActionContext): PreparedDeviceAction => {
	const { hexToDecimal, actions } = context;
	const action: PreparedDeviceAction = {
		_gui_purpose: 'action',
		r: 'dev:' + hexToDecimal + ':setup',
	};
	const set: Record<string, unknown> = { _isdata: true };
	const getAction: DeviceActionGetter = (...names) =>
		actions.find((actionEl) => names.includes(actionEl.name));

	for (const actionEl of actions) {
		const handler = COLOR_BULB_ACTION_HANDLERS[actionEl.name as ColorBulbActionName];
		if (handler) {
			handler({ action: actionEl, context, set, getAction });
		}
	}
	action.set = { ...set };
	return action;
};

export const prepareCCTBulbAction = (context: DeviceActionContext): PreparedDeviceAction => {
	const { hexToDecimal, actions } = context;
	const action: PreparedDeviceAction = {
		_gui_purpose: 'action',
		r: 'dev:' + hexToDecimal + ':setup',
	};
	const set: Record<string, unknown> = { _isdata: true };
	const getAction: DeviceActionGetter = (...names) =>
		actions.find((actionEl) => names.includes(actionEl.name));

	for (const actionEl of actions) {
		const handler = CCT_BULB_ACTION_HANDLERS[actionEl.name as CCTBulbActionName];
		if (handler) {
			handler({ action: actionEl, context, set, getAction });
		}
	}
	action.set = { ...set };
	return action;
};

export const isColorBulb = (type: string, mode: string, channelMode?: string): boolean =>
	COLOR_BULBS.includes(type) ||
	(type === 'SHRGBW2' && mode === 'color') ||
	(type === 'SNDC-0D4P10WW' && mode.indexOf('rgb') !== -1) ||
	(type === 'SPDC-0D5PE16EU' && channelMode === 'rgb') ||
	type === 'S3BL-C010007AEU' ||
	type === 'S3BL-D010009AEU' ||
	type === 'SHBDUO-1';

export const isCCTMode = (type: string, channelMode?: string, mode?: string): boolean => {
	if (isXMODDevice(type)) {
		return mode === 'cct' || channelMode === 'cct';
	}
	if (isCctOnlyDevice(type)) {
		return true;
	}
	return channelMode === 'cct' && isRgbwwPm(type);
};
